<script setup lang="ts">
import {
  autoUpdate,
  flip,
  offset,
  shift,
  useFloating,
} from '@floating-ui/vue';
import {
  computed,
  nextTick,
  ref,
  watch,
} from 'vue';

import {
  GrammarDivider,
  GrammarMenu,
  GrammarOverlayCard,
  GrammarQuaternaryButtonToggle,
  GrammarQuaternaryMenuButton,
} from '@nora-monorepo/grammar-ui';
import type { MenuItem } from '@nora-monorepo/grammar-ui';

import type {
  GrammarActionBoxExpose,
  GrammarActionBoxProps,
} from '#nora-ui/map/components/controls/types';

const props = defineProps<GrammarActionBoxProps>();

const emit = defineEmits<{
  select: [actionId: string | null];
}>();

const actionBoxRef = ref<HTMLElement | null>(null);
const actionBoxKey = ref(0);
const isVisible = ref(false);
const position = ref({ x: 0, y: 0 });
const selectedActionId = ref<string | null>(null);

const selectedActionIndex = computed(() => props.actions.findIndex(
  ({ id }) => id === selectedActionId.value,
));

const selectedActionMenuAnchor = computed(() =>
  actionBoxRef.value?.querySelectorAll('tpg-quaternary-button-toggle')[selectedActionIndex.value] ?? null,
);

const selectedActionMenuClass = computed(() => {
  if (props.actions.length === 1) {
    return 'self-center';
  }

  if (selectedActionIndex.value === 0) {
    return 'self-start';
  }

  if (selectedActionIndex.value === props.actions.length - 1) {
    return 'self-end';
  }

  return 'self-center';
});

const virtualElement = computed(() => {
  return {
    getBoundingClientRect: () => ({
      x: position.value.x,
      y: position.value.y,
      top: position.value.y,
      left: position.value.x,
      bottom: position.value.y,
      right: position.value.x,
      width: 0,
      height: 0,
    }),
  };
});

const { floatingStyles, update, x, y } = useFloating(virtualElement, actionBoxRef, {
  whileElementsMounted: autoUpdate,
  placement: 'bottom',
  strategy: 'fixed',
  middleware: [
    offset(props.offset ?? 10),
    flip(() => ({ boundary: props.boundary ?? 'clippingAncestors' })),
    shift(() => ({ boundary: props.boundary ?? 'clippingAncestors', padding: 5 })),
  ],
  transform: false,
});

// Grammar doesn't track its anchor moving. Trigger resize after the DOM update to reposition the open submenu.
watch([x, y], () => {
  if (!props.docked && selectedActionId.value !== null) {
    window.dispatchEvent(new Event('resize'));
  }
}, { flush: 'post' });

function open(nextPosition: { x: number; y: number }): void {
  position.value = nextPosition;
  selectedActionId.value = null;
  actionBoxKey.value++;
  isVisible.value = true;
  void nextTick(() => update());
}

function updatePosition(nextPosition: { x: number; y: number }): void {
  position.value = nextPosition;
  void nextTick(() => update());
}

function close(): void {
  isVisible.value = false;
  selectedActionId.value = null;
}

function selectAction(actionId: string): void {
  selectedActionId.value = selectedActionId.value === actionId ? null : actionId;
  emit('select', selectedActionId.value);
}

function navigateDockedMenu(event: KeyboardEvent): void {
  if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') {
    return;
  }

  const items = Array.from(actionBoxRef.value?.querySelectorAll<MenuItem>('tpg-menu-item') ?? [])
    .filter(item => !item.disabled);
  const index = items.findIndex(item => event.composedPath().includes(item));

  if (index < 0) {
    return;
  }

  event.preventDefault();
  event.stopPropagation();

  items[index + (event.key === 'ArrowDown' ? 1 : -1)]?.focus();
}

defineExpose<GrammarActionBoxExpose>({ close, open, updatePosition });
</script>

<template>
  <slot
    v-if="isVisible"
    name="preview"
    :position="position"
  />

  <div
    v-if="isVisible"
    ref="actionBoxRef"
    class="z-50"
    :class="docked ? 'absolute inset-x-4 bottom-4 flex max-h-[calc(100%-2rem)] flex-col' : 'absolute'"
    :style="docked ? undefined : floatingStyles"
    @click.stop
  >
    <div
      v-if="docked && selectedActionId"
      data-action-menu
      class="min-h-0 max-w-full overflow-y-auto"
      :class="selectedActionMenuClass"
      @keydown="navigateDockedMenu"
      @keydown.esc.stop="selectAction(selectedActionId)"
    >
      <GrammarMenu
        :key="selectedActionId"
        :anchor="selectedActionMenuAnchor"
        :is-open="true"
      >
        <slot
          :action-id="selectedActionId"
          :action-count="actions.length"
          :selected-action-id="selectedActionId"
          :selected-action-index="selectedActionIndex"
        />
      </GrammarMenu>
    </div>

    <GrammarOverlayCard
      :key="actionBoxKey"
      :class="{ 'w-full shrink-0': docked }"
      border-type="accent"
    >
      <div
        class="flex items-stretch"
        :class="{ 'w-full': docked }"
      >
        <template
          v-for="(action, index) in actions"
          :key="action.id"
        >
          <div
            class="flex justify-center"
            :class="docked ? 'min-w-0 flex-1' : 'min-w-32'"
          >
            <GrammarQuaternaryButtonToggle
              v-if="docked"
              class="block w-full"
              :icon-template="action.iconTemplate"
              :aria-label="action.label"
              icon-position="top"
              :label="action.label"
              :title="action.label"
              :selected="selectedActionId === action.id"
              @click="selectAction(action.id)"
            />
            <GrammarQuaternaryMenuButton
              v-else
              class="grid w-full"
              :icon-template="action.iconTemplate"
              icon-position="top"
              :label="action.label"
              :title="action.label"
              @click="selectAction(action.id)"
            >
              <slot
                :action-id="action.id"
                :action-count="actions.length"
                :selected-action-id="selectedActionId"
                :selected-action-index="selectedActionIndex"
              />
            </GrammarQuaternaryMenuButton>
          </div>
          <GrammarDivider
            v-if="index < actions.length - 1"
            orientation="vertical"
          />
        </template>
      </div>
    </GrammarOverlayCard>
  </div>
</template>
